import { Button, Group, Space } from '@mantine/core';
import { RuleEditor } from './Model';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDi, useDiContainer } from '@root/Services/DI';
import { ResourceGroupActionCard, ResourceGroupEditor } from './ResourceGroupActionCard';
import { PlatformService } from '@root/Services/PlatformService';
import { AwsOrgEntityEditor, AwsOrgEntityGroupActionCard } from './AWSOrgEntityGroupActionCard';
import { RuleEditCard } from './Design';
import { StandardRuleLayout } from './StandardRuleLayout';
import { FillerSwitch } from '@root/Design/Filler';

export function ResourceGroupRuleCard({ ruleEditor }: { ruleEditor: RuleEditor }) {
    const platformSvc = useDi(PlatformService);
    const [loading, setLoading] = useState(true);
    const [platforms, setPlatforms] = useState<Set<string>>();

    const selectType = useCallback(
        (type: 'AwsOUAccountGroup' | 'ResourceGroup') => {
            ruleEditor.rule!.Parameters!.Type = type;
        },
        [ruleEditor]
    );
    const selectAws = useCallback(() => selectType('AwsOUAccountGroup'), [selectType]);
    const selectAzure = useCallback(() => selectType('ResourceGroup'), [selectType]);
    const saved = !!ruleEditor.rule?.Id;

    useEffect(() => {
        platformSvc.init().then(() => {
            const platforms = platformSvc.getPlatforms();
            setPlatforms(platforms);
            if (!saved) {
                selectType(platforms.has('Aws') ? 'AwsOUAccountGroup' : 'ResourceGroup');
            }
            setLoading(false);
        });
    }, [platformSvc, selectType]);

    const type = ruleEditor.rule?.Parameters?.Type;

    return (
        <FillerSwitch loading={loading} noData={!platforms?.size}>
            {() => (
                <>
                    {platforms!.size <= 1 ? null : (
                        <>
                            <RuleEditCard accent title="Select Platform" description="Which cloud provider is this rule intended for?">
                                <Space h="md" />
                                <Group position="center" spacing="xl">
                                    <Button
                                        onClick={selectAws}
                                        sx={{ width: 130 }}
                                        variant={type === 'AwsOUAccountGroup' || saved ? 'filled' : 'outline'}
                                        disabled={saved && type !== 'AwsOUAccountGroup'}
                                    >
                                        AWS
                                    </Button>
                                    <Button
                                        onClick={selectAzure}
                                        sx={{ width: 130 }}
                                        variant={type === 'ResourceGroup' || saved ? 'filled' : 'outline'}
                                        disabled={saved && type !== 'ResourceGroup'}
                                    >
                                        Azure
                                    </Button>
                                </Group>
                            </RuleEditCard>
                            <Space h="md" />
                        </>
                    )}

                    <StandardRuleLayout ruleEditor={ruleEditor}>
                        {type === 'AwsOUAccountGroup' ? (
                            <AwsOrgEntityGroupActionCard ruleEditor={ruleEditor} />
                        ) : (
                            <ResourceGroupActionCard ruleEditor={ruleEditor} />
                        )}
                    </StandardRuleLayout>
                </>
            )}
        </FillerSwitch>
    );
}
